import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, PackageIcon, ShirtIcon, PlaneIcon, SparklesIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, TruckIcon, ShieldCheckIcon, BoxIcon, TagIcon, PaletteIcon, FileCheckIcon, FactoryIcon, SearchCheckIcon, ShipIcon, UsersIcon, DollarSignIcon, ZapIcon } from 'lucide-react';
export function CorporateTravelKits() {
  const [expandedKit, setExpandedKit] = useState<string | null>(null);
  const [attendees, setAttendees] = useState(50);
  const [selectedTier, setSelectedTier] = useState('premium');
  const [personalization, setPersonalization] = useState(true);
  const [freightMode, setFreightMode] = useState('sea');
  const kitContents = {
    carryOn: {
      icon: <PackageIcon className="h-8 w-8" data-id="element-1413" />,
      title: 'Carry-on / Weekender',
      image: 'https://images.unsplash.com/photo-1565026057447-bc90a3dceb87?w=800&q=80',
      items: ['Premium carry-on luggage (20-22")', 'Durable wheels and telescoping handle', 'TSA-approved locks', 'Interior organization pockets', 'Your logo embroidered or debossed']
    },
    apparel: {
      icon: <ShirtIcon className="h-8 w-8" data-id="element-1414" />,
      title: 'Apparel Set',
      image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&q=80',
      items: ['Branded polo or performance tee', 'Lightweight jacket or quarter-zip', 'Baseball cap or beanie', 'Moisture-wicking socks', 'Size-specific packaging']
    },
    essentials: {
      icon: <PlaneIcon className="h-8 w-8" data-id="element-1415" />,
      title: 'Travel Essentials',
      image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=800&q=80',
      items: ['Travel adapter (universal)', 'Portable charger (10,000mAh)', 'Noise-canceling earbuds', 'Eye mask and earplugs', 'Reusable water bottle', 'Travel-size toiletry kit']
    },
    keepsake: {
      icon: <SparklesIcon className="h-8 w-8" data-id="element-1416" />,
      title: 'Signature Keepsake',
      image: 'https://images.unsplash.com/photo-1517842645767-c639042777db?w=800&q=80',
      items: ['Premium notebook with custom cover', 'Engraved pen set', 'Custom challenge coin or pin', 'Personalized name badge holder', 'Event-specific commemorative item']
    }
  };
  const tiers = [{
    id: 'core',
    name: 'Core Kit',
    badge: null,
    description: 'Apparel + essentials + keepsake',
    leadTime: '6-8 weeks',
    includes: ['Branded apparel set (3 items)', 'Travel essentials bundle', 'Signature keepsake item', 'Custom packaging', 'Bulk shipment to HQ'],
    excludes: ['Carry-on luggage', 'Personal item bag'],
    priceRange: '$75-125'
  }, {
    id: 'premium',
    name: 'Premium Kit',
    badge: 'Most Popular',
    description: 'Carry-on + apparel + essentials + keepsake',
    leadTime: '8-10 weeks',
    includes: ['Premium carry-on luggage', 'Branded apparel set (3 items)', 'Complete travel essentials', 'Signature keepsake item', 'Optional name/role tags', 'Packing cubes included', 'Custom packaging'],
    excludes: [],
    priceRange: '$175-275'
  }, {
    id: 'exec',
    name: 'Executive Kit',
    badge: null,
    description: 'Premium carry-on + personal item + elevated materials',
    leadTime: '10-12 weeks',
    includes: ['Premium carry-on (higher grade)', 'Matching personal item bag', 'Luxury apparel set (4+ items)', 'Premium travel essentials', 'Executive keepsake collection', 'Full personalization (name embroidery)', 'Premium packing cubes', 'Gift-quality packaging'],
    excludes: [],
    priceRange: '$325-475'
  }];
  const timeline = [{
    step: 1,
    icon: <BoxIcon className="h-6 w-6" data-id="element-1417" />,
    title: 'Select Tier + Quantity',
    description: 'Choose kit level and set ship-to-HQ date'
  }, {
    step: 2,
    icon: <PaletteIcon className="h-6 w-6" data-id="element-1418" />,
    title: 'Upload Branding',
    description: 'Logo, event name, and color palette'
  }, {
    step: 3,
    icon: <FileCheckIcon className="h-6 w-6" data-id="element-1419" />,
    title: 'Approve Sample',
    description: 'Physical sample shipped for approval'
  }, {
    step: 4,
    icon: <FactoryIcon className="h-6 w-6" data-id="element-1420" />,
    title: 'Production + Branding',
    description: 'Manufacturing and logo application'
  }, {
    step: 5,
    icon: <SearchCheckIcon className="h-6 w-6" data-id="element-1421" />,
    title: 'QC Inspection',
    description: 'Mandatory quality control before shipping'
  }, {
    step: 6,
    icon: <ShipIcon className="h-6 w-6" data-id="element-1422" />,
    title: 'Bulk Shipment',
    description: 'Consolidated delivery to your HQ'
  }, {
    step: 7,
    icon: <UsersIcon className="h-6 w-6" data-id="element-1423" />,
    title: 'Reorder Window',
    description: 'Late joiners accommodation period'
  }];
  const calculatePrice = () => {
    const basePrices = {
      core: 100,
      premium: 225,
      exec: 400
    };
    let price = basePrices[selectedTier as keyof typeof basePrices];
    // Volume discount
    if (attendees >= 100) price *= 0.85;else if (attendees >= 50) price *= 0.9;
    // Personalization
    if (personalization) price += 15;
    // Freight
    if (freightMode === 'air') price += 25;
    return Math.round(price);
  };
  const getTimeline = () => {
    if (selectedTier === 'core') return '6-8 weeks';
    if (selectedTier === 'premium') return '8-10 weeks';
    return '10-12 weeks';
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1424">
      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[#0099A1] to-[#006D73] overflow-hidden" data-id="element-1425">
        <div className="absolute inset-0 opacity-10" data-id="element-1426">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-1427"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1428">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1429">
            <div data-id="element-1430">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-1431">
                Premium Corporate Travel Kits — Delivered to Your HQ Before
                Departure
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed" data-id="element-1432">
                We source, brand, kit, and bulk-ship attendee kits so your team
                travels with everything.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1433">
                <Button variant="primary" size="lg" to="/contact" className="bg-white text-[#0099A1] hover:bg-white/90 border-0" data-id="element-1434">
                  Build a Kit
                </Button>
                <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1435">
                  Get Quote
                </Button>
              </div>
            </div>
            <div className="relative" data-id="element-1436">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-1437">
                <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80" alt="Travel kit mockup" className="w-full h-[500px] object-cover" data-id="element-1438" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-1439"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-1440">
                  <div className="grid grid-cols-2 gap-3" data-id="element-1441">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1442">
                      <PackageIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1443" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1444">
                        Carry-on
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1445">
                      <ShirtIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1446" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1447">
                        Apparel
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1448">
                      <PlaneIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1449" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1450">
                        Essentials
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1451">
                      <SparklesIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1452" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1453">
                        Keepsake
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-20 bg-white" data-id="element-1454">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1455">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1456">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1457">
              What's Inside the Kit
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1458">
              Every kit is thoughtfully curated with premium items your team
              will actually use
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-1459">
            {Object.entries(kitContents).map(([key, content]) => <div key={key} className="group" data-id="element-1460">
                <div className="relative overflow-hidden rounded-2xl mb-4 shadow-lg" data-id="element-1461">
                  <img src={content.image} alt={content.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-1462" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-1463"></div>
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-1464">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#0099A1] mb-3" data-id="element-1465">
                      {content.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white" data-id="element-1466">
                      {content.title}
                    </h3>
                  </div>
                </div>
                <button onClick={() => setExpandedKit(expandedKit === key ? null : key)} className="flex items-center gap-2 text-[#0099A1] font-medium text-sm hover:text-[#006D73] transition-colors" data-id="element-1467">
                  <span data-id="element-1468">View Details</span>
                  {expandedKit === key ? <ChevronUpIcon className="h-4 w-4" data-id="element-1469" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-1470" />}
                </button>
              </div>)}
          </div>

          {/* Expandable Checklist */}
          {expandedKit && <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 border-2 border-[#0099A1]/20 shadow-lg" data-id="element-1471">
              <h4 className="text-2xl font-bold text-[#374047] mb-6 flex items-center gap-3" data-id="element-1472">
                {kitContents[expandedKit as keyof typeof kitContents].icon}
                {kitContents[expandedKit as keyof typeof kitContents].title}
              </h4>
              <ul className="space-y-4" data-id="element-1473">
                {kitContents[expandedKit as keyof typeof kitContents].items.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-1474">
                      <CheckIcon className="h-6 w-6 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1475" />
                      <span className="text-[#374047] text-lg" data-id="element-1476">{item}</span>
                    </li>)}
              </ul>
            </div>}
        </div>
      </section>

      {/* Kit Tiers */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1477">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1478">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1479">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1480">
              Choose Your Kit Tier
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1481">
              Select the package that fits your event and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1482">
            {tiers.map(tier => <Card key={tier.id} className={`border-2 ${tier.badge ? 'border-[#0099A1] shadow-2xl scale-105' : 'border-gray-200'} relative overflow-hidden`} data-id="element-1483">
                {tier.badge && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10" data-id="element-1484">
                    <span className="inline-flex items-center px-6 py-2 bg-[#FF6B65] text-white text-sm font-bold rounded-full shadow-lg" data-id="element-1485">
                      {tier.badge}
                    </span>
                  </div>}
                <CardHeader className={`${tier.badge ? 'bg-[#0099A1] pt-8' : 'bg-[#374047]'} text-white p-8`} data-id="element-1486">
                  <h3 className="text-2xl font-bold mb-3" data-id="element-1487">{tier.name}</h3>
                  <p className="text-white/90 mb-4" data-id="element-1488">{tier.description}</p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 rounded-lg px-3 py-2 w-fit" data-id="element-1489">
                    <ClockIcon className="h-4 w-4" data-id="element-1490" />
                    <span data-id="element-1491">Lead time: {tier.leadTime}</span>
                  </div>
                </CardHeader>
                <CardBody className="p-8" data-id="element-1492">
                  <div className="text-center mb-8 pb-8 border-b-2 border-gray-100" data-id="element-1493">
                    <div className="text-4xl font-bold text-[#374047] mb-2" data-id="element-1494">
                      {tier.priceRange}
                    </div>
                    <div className="text-sm text-[#374047]/60" data-id="element-1495">per kit</div>
                  </div>

                  <h4 className="font-bold text-[#374047] mb-4 text-lg" data-id="element-1496">
                    What's Included:
                  </h4>
                  <ul className="space-y-3 mb-8" data-id="element-1497">
                    {tier.includes.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-1498">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1499" />
                        <span className="text-[#374047]" data-id="element-1500">{item}</span>
                      </li>)}
                  </ul>

                  {tier.excludes.length > 0 && <>
                      <h4 className="font-bold text-[#374047]/60 mb-4" data-id="element-1501">
                        Not Included:
                      </h4>
                      <ul className="space-y-2 mb-8" data-id="element-1502">
                        {tier.excludes.map((item, index) => <li key={index} className="flex items-start gap-3 text-[#374047]/60" data-id="element-1503">
                            <span className="text-lg" data-id="element-1504">•</span>
                            <span data-id="element-1505">{item}</span>
                          </li>)}
                      </ul>
                    </>}

                  <Button variant={tier.badge ? 'primary' : 'secondary'} className="w-full" to="/contact" data-id="element-1506">
                    Select {tier.name}
                  </Button>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white" data-id="element-1507">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1508">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1509">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1510">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1511">
              Direct sourcing timeline from selection to delivery
            </p>
          </div>

          <div className="max-w-6xl mx-auto" data-id="element-1512">
            <div className="grid md:grid-cols-7 gap-6" data-id="element-1513">
              {timeline.map((item, index) => <div key={item.step} className="relative" data-id="element-1514">
                  <div className="flex flex-col items-center text-center" data-id="element-1515">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-full flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-lg" data-id="element-1516">
                      {item.step}
                    </div>
                    <div className="w-14 h-14 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] mb-4" data-id="element-1517">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-[#374047] mb-2" data-id="element-1518">
                      {item.title}
                    </h4>
                    <p className="text-sm text-[#374047]/60" data-id="element-1519">
                      {item.description}
                    </p>
                  </div>
                  {index < timeline.length - 1 && <div className="hidden md:block absolute top-10 left-full w-full h-1 bg-gradient-to-r from-[#0099A1] to-[#0099A1]/20 -z-10" data-id="element-1520"></div>}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Lead Time */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1521">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1522">
          <div className="max-w-4xl mx-auto" data-id="element-1523">
            <div className="text-center mb-16" data-id="element-1524">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1525">
                Lead Time & Reliability
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-1526">
              <Card className="border-2 border-[#0099A1]/30 shadow-lg" data-id="element-1527">
                <CardBody className="p-10" data-id="element-1528">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1529">
                    <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center" data-id="element-1530">
                      <ClockIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1531" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1532">
                      Standard Timeline
                    </h3>
                  </div>
                  <p className="text-3xl font-bold text-[#0099A1] mb-3" data-id="element-1533">
                    6-10 weeks door-to-door
                  </p>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1534">
                    Recommended for best pricing and full customization options
                  </p>
                </CardBody>
              </Card>

              <Card className="border-2 border-[#FF6B65]/30 shadow-lg" data-id="element-1535">
                <CardBody className="p-10" data-id="element-1536">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1537">
                    <div className="w-16 h-16 bg-[#FF6B65]/10 rounded-full flex items-center justify-center" data-id="element-1538">
                      <ZapIcon className="h-8 w-8 text-[#FF6B65]" data-id="element-1539" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1540">
                      Rush Option
                    </h3>
                  </div>
                  <p className="text-3xl font-bold text-[#FF6B65] mb-3" data-id="element-1541">
                    Possible with simpler kit
                  </p>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1542">
                    Higher freight costs apply; limited customization
                  </p>
                </CardBody>
              </Card>
            </div>

            <div className="bg-white rounded-2xl p-10 border-2 border-[#0099A1]/30 shadow-lg" data-id="element-1543">
              <div className="flex items-start gap-6" data-id="element-1544">
                <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1545">
                  <ShieldCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1546" />
                </div>
                <div data-id="element-1547">
                  <h4 className="font-bold text-[#374047] mb-3 text-xl" data-id="element-1548">
                    Quality Control Inspection
                  </h4>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1549">
                    QC inspection is mandatory before shipping. Every kit is
                    checked for quality, branding accuracy, and completeness
                    before leaving the facility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="py-20 bg-white" data-id="element-1550">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1551">
          <div className="max-w-4xl mx-auto" data-id="element-1552">
            <div className="text-center mb-16" data-id="element-1553">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1554">
                Estimate Your Kit Cost
              </h2>
              <p className="text-lg text-[#374047]/70" data-id="element-1555">
                Get a quick estimate based on your requirements
              </p>
            </div>

            <Card className="border-2 border-[#0099A1]/30 shadow-xl" data-id="element-1556">
              <CardBody className="p-10" data-id="element-1557">
                <div className="grid md:grid-cols-2 gap-8 mb-10" data-id="element-1558">
                  <div data-id="element-1559">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1560">
                      Number of Attendees
                    </label>
                    <input type="number" value={attendees} onChange={e => setAttendees(parseInt(e.target.value) || 0)} className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-lg" min="1" data-id="element-1561" />
                  </div>

                  <div data-id="element-1562">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1563">
                      Kit Tier
                    </label>
                    <select value={selectedTier} onChange={e => setSelectedTier(e.target.value)} className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-lg" data-id="element-1564">
                      <option value="core" data-id="element-1565">Core Kit</option>
                      <option value="premium" data-id="element-1566">Premium Kit</option>
                      <option value="exec" data-id="element-1567">Executive Kit</option>
                    </select>
                  </div>

                  <div data-id="element-1568">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1569">
                      Personalization
                    </label>
                    <div className="flex gap-4" data-id="element-1570">
                      <button onClick={() => setPersonalization(true)} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${personalization ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1571">
                        Yes
                      </button>
                      <button onClick={() => setPersonalization(false)} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${!personalization ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1572">
                        No
                      </button>
                    </div>
                  </div>

                  <div data-id="element-1573">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1574">
                      Freight Mode
                    </label>
                    <div className="flex gap-4" data-id="element-1575">
                      <button onClick={() => setFreightMode('sea')} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${freightMode === 'sea' ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1576">
                        Sea
                      </button>
                      <button onClick={() => setFreightMode('air')} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${freightMode === 'air' ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1577">
                        Air
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl p-10 text-white" data-id="element-1578">
                  <div className="grid md:grid-cols-3 gap-8" data-id="element-1579">
                    <div className="text-center" data-id="element-1580">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1581">
                        Estimated Cost Per Kit
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1582">
                        ${calculatePrice()}
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1583">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1584">
                        Total Estimated Cost
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1585">
                        ${(calculatePrice() * attendees).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1586">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1587">
                        Recommended Timeline
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1588">{getTimeline()}</div>
                    </div>
                  </div>
                  <div className="mt-8 pt-8 border-t border-white/20 text-center" data-id="element-1589">
                    <p className="opacity-90 text-lg" data-id="element-1590">
                      Final pricing depends on exact specifications, branding
                      complexity, and current freight rates
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0099A1] to-[#006D73]" data-id="element-1591">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1592">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1593">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1594">
              Get Your Kit Plan in 48 Hours
            </h2>
            <p className="text-2xl text-white/90 mb-10" data-id="element-1595">
              Quote + timeline + sample plan delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1596">
              <Button variant="primary" size="lg" to="/contact" className="bg-white text-[#0099A1] hover:bg-white/90 border-0" data-id="element-1597">
                Request Quote
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1598">
                Talk to Ops
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1599" />
    </div>;
}